import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import { getUrlForImage } from "../cubeComponent/constants/globalFunctions";
import { CONSTANTS } from "../cubeComponent/constants/globalVariable";
import axiosWrapper from "../services/axiosWrapper";
import type {
  GetUserDetailsResponse,
  CheckIfAuthenticatedResponse,
  InitiateSignupEmailVerificationResponse,
} from "@ServerTypes/accounts/accounts.schema";
import { setUserDetails } from "../reducers/userDetailsSlice";
import { setCheckIfAuthenticated } from "../reducers/authentication";
import { clearCsrfToken } from "@Services/axiosShared";

const saveUserDetails = setUserDetails;

export function getUserDetails() {
  return (dispatch: AppDispatch) => {
    // deliberately floating thunk: nothing dispatches this and then reads a result, and a rejected
    // request must stay unhandled so the global handler still reports it.
    void axiosWrapper.get<GetUserDetailsResponse>(`${getBackendUrl()}/accounts/getUserDetails`).then((response) => {
      // `getUserDetailsResponse` now declares the one-element `result` array on both arms, so this
      // is a plain narrow-then-index — no cast, and a future rename of the key is a compile error
      // here rather than a silently-undefined read. (It used to be a `data?: UserDetails[]` cast
      // bolted onto an opaque `statusResponse`.)
      const res = response.data.status ? response.data.result[0] : null;
      if (res) {
        const organisation = res.organisation ?? {};
        let data = {
          ...res,
          lastName: res.lastName ? res.lastName : "",
          profilePicUrl: res.profilePic ? getUrlForImage(res) : "",
          organisation: {
            ...organisation,
            // ⚠️ ALWAYS "" TODAY. `/accounts/getUserDetails` does not emit `organisation.logo`: its
            // portal-scoped aggregation projects `{ id, name }` only (account.mongo.helper.ts:294)
            // and its by-id exit does not project `organisation` at all. Kept as a narrowed
            // passthrough read rather than deleted because `userDetailsWireRow` is a `looseObject`
            // and this route is also apikey-reachable. Sourcing a real logo means adding it to that
            // projection.
            logoUrl: typeof organisation.logo === "string" ? getUrlForImage(organisation, CONSTANTS.ORGANIZATION) : "",
          },
          signatureUrl: res.signature
            ? `${getBackendUrl()}/fileSystemServer/getSignDocs/${res.signature}?${Date.now()}`
            : "",
          messageFooterContent: res.messageFooter ?? "",
          status: true,
        };
        // Injected by the desktop shell's preload script; absent in the browser, hence the
        // optional calls. Named at the read rather than declared globally.
        (
          window as Window & { electronAPI?: { setUserDataInDesktop?: (d: unknown) => void } }
        ).electronAPI?.setUserDataInDesktop?.(data);
        dispatch(saveUserDetails(data));
      } else {
        dispatch(saveUserDetails({ status: false }));
      }
    });
  };
}

export async function initiateSignupEmailVerification(email: string) {
  return await axiosWrapper.post<InitiateSignupEmailVerificationResponse>(
    `${getBackendUrl()}/accounts/initiateSignupEmailVerification`,
    {
      email,
    },
  );
}

export function getCheckIfAuthenticated() {
  return (dispatch: AppDispatch) => {
    // deliberately floating thunk: the result is delivered through the store, not to the dispatcher, and a rejected
    // request must stay unhandled so the global handler still reports it.
    void axiosWrapper
      .get<CheckIfAuthenticatedResponse>(`${getBackendUrl()}/accounts/checkIfAuthenticated`, {
        withCredentials: true,
      })
      .then((response) => {
        clearCsrfToken();
        dispatch(setCheckIfAuthenticated({ data: response.data }));
        return response.data;
      });
  };
}
